import React, { ImgHTMLAttributes } from 'react';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import { buildContentfulApiImgSrc, DEFAULT_API_OPTIONS } from './buildContentfulApiImgSrc';
import { styles } from './ContentfulImage.css';
import { useConfigContext } from '../context/ConfigProvider';

export const MAX_IMAGE_WIDTH = 4000;

export type ContentfulImageProps = {
  aspectRatio?: number;
  originalWidth?: number;
  isProgressive?: boolean;
  fillParent?: boolean;
  imgOverlay?: React.ReactNode;
  shouldUseCustomImageQuality?: boolean;
} & ImgHTMLAttributes<HTMLImageElement>;

export const ContentfulImage: React.FC<ContentfulImageProps> = ({
  src,
  aspectRatio,
  isProgressive = true,
  fillParent = false,
  className,
  imgOverlay,
  originalWidth = 0,
  ...rest
}) => {
  const { contentfulImageQuality } = useConfigContext();

  if (!src) return null;

  const resizeImageSrc = (width: number) =>
    buildContentfulApiImgSrc(src, {
      ...DEFAULT_API_OPTIONS,
      w: width,
      h: Math.ceil(width / (aspectRatio || 1)),
      q: Number(contentfulImageQuality),
    });

  // Contentful images can be up to 4000px wide
  const safeOriginalWidth = Math.min(originalWidth, MAX_IMAGE_WIDTH);

  const imgProps = {
    sizes: '(max-width: 1124px) 1124px, 100vw' /* Default image sizing: max-width: 1124px (page-width), width: 100vw */,
    ...rest,
    className: classNames([styles.image({ fillParent }), className]),
    srcSet: [128, 384, 768, 1124, 1536, 2248, safeOriginalWidth]
      .filter(num => num && num <= (safeOriginalWidth || Number.MAX_SAFE_INTEGER))
      .map(w => `${resizeImageSrc(w)} ${w}w`)
      .join(', '),
    src: resizeImageSrc(1124),
  };

  return (
    <>
      {isProgressive ? (
        <ProgressiveImage {...imgProps} placeholderSrc={resizeImageSrc(48)} />
      ) : (
        <img {...imgProps} alt={imgProps.alt} />
      )}
      {imgOverlay}
    </>
  );
};
